import React from 'react';
import { getRuntimeConfig } from '../config/runtimeConfig';
import {
  makeStyles,
  shorthands,
  Text,
  Card,
  CardHeader,
  tokens,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel } from
'@fluentui/react-components';
import {
  BookOpen24Regular,
  Shield24Regular,
  Globe24Regular,
  Settings24Regular,
  People24Regular,
  ArrowSync24Regular } from
'@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXL,
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0')
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS
  },
  subtitle: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5'
  },
  section: {
    marginBottom: tokens.spacingVerticalXXL
  },
  sectionTitle: {
    marginBottom: tokens.spacingVerticalL,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM
  },
  card: {
    marginBottom: tokens.spacingVerticalL,
    ...shorthands.padding('24px')
  },
  step: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px')
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: '#0047AB',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    flexShrink: 0
  },
  stepContent: {
    flex: 1
  },
  codeBlock: {
    fontFamily: 'Consolas, Monaco, monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    fontSize: '14px',
    marginTop: tokens.spacingVerticalS,
    overflowX: 'auto'
  }
});

export const UserGuide: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.container} data-testid="user-guide-container-div">
      <div className={styles.header} data-testid="user-guide-header-div">
        <div className={styles.title} data-testid="user-guide-title-div">
          <BookOpen24Regular data-testid="user-guide-book-open24-regular" />
          <Text size={600} weight="semibold" data-testid="user-guide-user-guide-text">User Guide</Text>
        </div>
        <Text className={styles.subtitle} data-testid="user-guide-learn-how-to-use-the-adm-text">
          Learn how to use the ADM (Approved Domain Management) system to manage Teams external federation policies
        </Text>
      </div>

      {/* Getting Started */}
      <div className={styles.section} data-testid="user-guide-section-div">
        <div className={styles.sectionTitle} data-testid="user-guide-section-title-div">
          <Shield24Regular data-testid="user-guide-shield24-regular" />
          <Text size={500} weight="semibold" data-testid="user-guide-getting-started-text">Getting Started</Text>
        </div>
        <Card className={styles.card} data-testid="user-guide-adm-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-overview-text">Overview</Text>}
            description="Understanding the ADM system" data-testid="user-guide-adm-card-header" />

          <Text style={{ marginBottom: '16px' }} data-testid="user-guide-the-adm-approved-domain-management-system-text">
            The ADM (Approved Domain Management) system helps organizations manage Microsoft Teams external federation 
            policies by automating domain approvals through integration with the Banking Domain Feed.
          </Text>
          
          <div className={styles.step} data-testid="user-guide-step-div">
            <div className={styles.stepNumber} data-testid="user-guide-step-number-div">1</div>
            <div className={styles.stepContent} data-testid="user-guide-step-content-div">
              <Text weight="semibold" data-testid="user-guide-view-policies-text">View Policies</Text>
              <Text size={300} data-testid="user-guide-getting-started-step1-description">
                Navigate to the Policies page to view all configured Teams external access policies. 
                Managed policies are automatically synchronized with the domain feed.
              </Text>
            </div>
          </div>

          <div className={styles.step} data-testid="user-guide-step-2-div">
            <div className={styles.stepNumber} data-testid="user-guide-step-number-2-div">2</div>
            <div className={styles.stepContent} data-testid="user-guide-step-content-2-div">
              <Text weight="semibold" data-testid="user-guide-manage-domains-text">Manage Domains</Text>
              <Text size={300} data-testid="user-guide-getting-started-step2-description">
                The Domains page shows all approved banking domains from the domain feed. 
                New domains are automatically added when they appear in the feed.
              </Text>
            </div>
          </div>

          <div className={styles.step} data-testid="user-guide-step-3-div">
            <div className={styles.stepNumber} data-testid="user-guide-step-number-3-div">3</div>
            <div className={styles.stepContent} data-testid="user-guide-step-content-3-div">
              <Text weight="semibold" data-testid="user-guide-configure-settings-text">Configure Settings</Text>
              <Text size={300} data-testid="user-guide-getting-started-step3-description">
                Use the Settings page to configure domain feed integration, drift monitoring, 
                and email notifications for domain changes.
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* Key Features */}
      <div className={styles.section} data-testid="user-guide-section-2-div">
        <div className={styles.sectionTitle} data-testid="user-guide-section-title-2-div">
          <Globe24Regular data-testid="user-guide-globe24-regular" />
          <Text size={500} weight="semibold" data-testid="user-guide-key-features-text">Key Features</Text>
        </div>
        
        <Accordion multiple data-testid="user-guide-accordion">
          <AccordionItem value="1" data-testid="user-guide-domain-synchronization-accordion-item">
            <AccordionHeader data-testid="user-guide-domain-synchronization-accordion-header">Domain Synchronization</AccordionHeader>
            <AccordionPanel data-testid="user-guide-domain-synchronization-accordion-panel">
              <Text style={{ marginBottom: '12px' }} data-testid="user-guide-the-system-automatically-synchronizes-domains-from-text">
                The system automatically synchronizes domains from the Banking Domain Feed:
              </Text>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }} data-testid="user-guide-domain-sync-feature-list-ul">
                <li data-testid="user-guide-domain-sync-feature-auto-updates-li">Automatic updates every configured interval</li>
                <li data-testid="user-guide-domain-sync-feature-manual-sync-li">Manual sync available through the UI</li>
                <li data-testid="user-guide-domain-sync-feature-validation-metadata-li">Support for domain validation and metadata</li>
                <li data-testid="user-guide-domain-sync-feature-bulk-operations-li">Bulk operations for efficiency</li>
              </ul>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem value="2" data-testid="user-guide-drift-detection-accordion-item">
            <AccordionHeader data-testid="user-guide-drift-detection-accordion-header">Drift Detection</AccordionHeader>
            <AccordionPanel data-testid="user-guide-drift-detection-accordion-panel">
              <Text style={{ marginBottom: '12px' }} data-testid="user-guide-monitor-and-resolve-differences-between-teams-text">
                Monitor and resolve differences between Teams policies and ADM configuration using snapshot-based field-by-field comparison:
              </Text>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }} data-testid="user-guide-drift-detection-feature-list-ul">
                <li data-testid="user-guide-drift-detection-feature-automatic-detection-li">Automatic detection every 5 minutes via unified drift+sync workflow</li>
                <li data-testid="user-guide-drift-detection-feature-email-notifications-li">Email notifications for drift (domain added, removed, modified, or policy deleted)</li>
                <li data-testid="user-guide-drift-detection-feature-snapshot-comparison-li">Snapshot-based comparison detecting changes in domains, groups, ranks, and settings</li>
                <li data-testid="user-guide-drift-detection-feature-approval-modes-li">Manual approval mode or bypass mode for automatic sync</li>
                <li data-testid="user-guide-drift-detection-feature-resolution-actions-li">One-click resolution options (Accept/Dismiss)</li>
                <li data-testid="user-guide-drift-detection-feature-audit-trail-li">Comprehensive audit trail for all changes</li>
              </ul>
              <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-configure-drift-monitoring-in-settings-text">
                Configure drift monitoring in Settings → Drift Monitoring Settings, including bypass notifications for automatic synchronization.
              </Text>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem value="3" data-testid="user-guide-policy-management-accordion-item">
            <AccordionHeader data-testid="user-guide-policy-management-accordion-header">Policy Management</AccordionHeader>
            <AccordionPanel data-testid="user-guide-policy-management-accordion-panel">
              <Text style={{ marginBottom: '12px' }} data-testid="user-guide-manage-teams-external-access-policies-efficiently-text">
                Manage Teams external access policies efficiently:
              </Text>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }} data-testid="user-guide-policy-management-feature-list-ul">
                <li data-testid="user-guide-policy-management-feature-create-configure-li">Create and configure policies with automatic domain assignment</li>
                <li data-testid="user-guide-policy-management-feature-assign-security-groups-li">Assign security groups with human-readable group names</li>
                <li data-testid="user-guide-policy-management-feature-set-rankings-li">Set policy rankings for proper precedence</li>
                <li data-testid="user-guide-policy-management-feature-bulk-domain-assignment-li">Bulk domain assignments from the PPE feed</li>
                <li data-testid="user-guide-policy-management-feature-group-display-names-li">View group display names instead of IDs for easier management</li>
                <li data-testid="user-guide-policy-management-feature-sync-status-li">Track policy synchronization status in real-time</li>
              </ul>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem value="4" data-testid="user-guide-audit-compliance-accordion-item">
            <AccordionHeader data-testid="user-guide-audit-compliance-accordion-header">Audit & Compliance</AccordionHeader>
            <AccordionPanel data-testid="user-guide-audit-compliance-accordion-panel">
              <Text style={{ marginBottom: '12px' }} data-testid="user-guide-comprehensive-audit-logging-for-compliance-text">
                Comprehensive audit logging for compliance:
              </Text>
              <ul style={{ marginLeft: '20px', lineHeight: '1.8' }} data-testid="user-guide-audit-compliance-feature-list-ul">
                <li data-testid="user-guide-audit-compliance-feature-action-logging-li">All actions are logged</li>
                <li data-testid="user-guide-audit-compliance-feature-user-attribution-li">User attribution and timestamps</li>
                <li data-testid="user-guide-audit-compliance-feature-exportable-reports-li">Exportable audit reports</li>
                <li data-testid="user-guide-audit-compliance-feature-azure-monitor-integration-li">Integration with Azure Monitor</li>
              </ul>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Common Tasks */}
      <div className={styles.section} data-testid="user-guide-section-3-div">
        <div className={styles.sectionTitle} data-testid="user-guide-section-title-3-div">
          <Settings24Regular data-testid="user-guide-settings24-regular" />
          <Text size={500} weight="semibold" data-testid="user-guide-common-tasks-text">Common Tasks</Text>
        </div>

        <Card className={styles.card} data-testid="user-guide-sync-domains-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-sync-domains-from-ppe-feed-text">Sync Domains from PPE Feed</Text>} data-testid="user-guide-sync-domains-card-header" />

          <Text data-testid="user-guide-the-system-automatically-syncs-with-the-text">The system automatically syncs with the PPE Workspace Teams Feed. To manually synchronize:</Text>
          <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-sync-domains-steps-ol">
            <li data-testid="user-guide-sync-domains-step-open-domains-page">Navigate to the Domains page</li>
            <li data-testid="user-guide-sync-domains-step-click-sync-now-li">Click the "Sync Now" button in the header</li>
            <li data-testid="user-guide-sync-domains-step-fetch-feed-li">The system will fetch domains from the PPE feed</li>
            <li data-testid="user-guide-sync-domains-step-validate-add-li">New domains are automatically validated and added</li>
            <li data-testid="user-guide-sync-domains-step-review-results-li">Review the sync results showing added/removed domains</li>
          </ol>
          <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-note-the-ppe-feed-is-configured-text">
            Note: The PPE feed is configured as the primary domain source and syncs every 6 hours automatically.
          </Text>
        </Card>

        <Card className={styles.card} data-testid="user-guide-policy-group-assignments-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-view-policy-group-assignments-text">View Policy Group Assignments</Text>} data-testid="user-guide-policy-group-assignments-card-header" />

          <Text data-testid="user-guide-policy-group-assignments-description">The Policies page displays security groups with their human-readable names:</Text>
          <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-policy-group-assignment-steps-ol">
            <li data-testid="user-guide-policy-group-assignment-step-open-policies-page">Navigate to the Policies page</li>
            <li data-testid="user-guide-policy-group-assignment-step-check-user-group-column-li">Look at the "User Group" column to see assigned groups</li>
            <li data-testid="user-guide-policy-group-assignment-step-resolve-azure-names-li">Group names are automatically resolved from Azure AD</li>
            <li data-testid="user-guide-policy-group-assignment-step-fallback-to-id-li">If a group name cannot be resolved, the ID will be shown as fallback</li>
          </ol>
          <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-note-group-names-are-cached-for-text">
            Note: Group names are cached for performance. Changes in Azure AD may take time to reflect.
          </Text>
        </Card>

        <Card className={styles.card} data-testid="user-guide-resolve-drift-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-resolve-policy-drift-text">Resolve Policy Drift</Text>} data-testid="user-guide-resolve-drift-card-header" />

          <Text data-testid="user-guide-when-drift-is-detected-text">When drift is detected:</Text>
          <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-resolve-drift-steps-ol">
            <li data-testid="user-guide-resolve-drift-step-notification-banner-li">A notification banner appears at the top of the page</li>
            <li data-testid="user-guide-resolve-drift-step-view-details-li">Click "View Details" to see the differences</li>
            <li data-testid="user-guide-resolve-drift-step-accept-or-reject-li">Choose "Accept Changes" to update Teams or "Reject" to keep current state</li>
            <li data-testid="user-guide-resolve-drift-step-confirm-action-li">Confirm your action when prompted</li>
          </ol>
        </Card>

        <Card className={styles.card} data-testid="user-guide-configure-email-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-configure-email-notifications-text">Configure Email Notifications</Text>} data-testid="user-guide-configure-email-card-header" />

          <Text data-testid="user-guide-to-set-up-email-alerts-for-text">To set up email alerts for drift detection and other events:</Text>
          <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-configure-email-steps-ol">
            <li data-testid="user-guide-configure-email-step-open-general-config-li">Go to Settings → General Configuration</li>
            <li data-testid="user-guide-configure-email-step-open-email-section-li">Scroll to the "Email Notifications" section</li>
            <li data-testid="user-guide-configure-email-step-enable-notifications-li">Toggle "Enable Notifications" to turn on email alerts</li>
            <li data-testid="user-guide-configure-email-step-enter-recipient-li">Enter the notification email address (e.g., admin@yourdomain.com)</li>
            <li data-testid="user-guide-configure-email-step-save-li">Click "Save" to apply changes</li>
          </ol>
          <Text style={{ marginTop: '12px', fontSize: '14px', fontWeight: '600' }} data-testid="user-guide-notification-types-text">
            Notification Types:
          </Text>
          <ul style={{ marginLeft: '20px', marginTop: '8px', lineHeight: '1.8' }} data-testid="user-guide-notification-types-list-ul">
            <li data-testid="user-guide-notification-type-policy-drift-li"><strong data-testid="user-guide-notification-type-policy-drift-label-strong">Policy Drift Detection:</strong> Automatic emails when drift is detected (domains added, removed, or modified)</li>
            <li data-testid="user-guide-notification-type-domain-approval-li"><strong data-testid="user-guide-notification-type-domain-approval-label-strong">Domain Approval Requests:</strong> Notifications when new domains need approval</li>
            <li data-testid="user-guide-notification-type-system-alerts-li"><strong data-testid="user-guide-notification-type-system-alerts-label-strong">System Alerts:</strong> Critical errors and system status changes</li>
          </ul>
          <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-note-drift-emails-are-sent-automatically-text">
            Note: Drift emails are sent automatically every 5 minutes when the unified drift+sync workflow detects changes.
          </Text>
        </Card>

        <Card className={styles.card} data-testid="user-guide-drift-bypass-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-configure-drift-bypass-auto-sync-text">Configure Drift Bypass (Auto-Sync)</Text>} data-testid="user-guide-drift-bypass-card-header" />

          <Text data-testid="user-guide-to-enable-automatic-synchronization-without-manual-text">To enable automatic synchronization without manual drift approval:</Text>
          <ol style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-drift-bypass-steps-ol">
            <li data-testid="user-guide-drift-bypass-step-open-drift-settings-li">Go to Settings → Drift Monitoring Settings</li>
            <li data-testid="user-guide-drift-bypass-step-enable-bypass-li">Toggle "Bypass Drift Notifications" to enable auto-sync</li>
            <li data-testid="user-guide-drift-bypass-step-auto-sync-behavior-li">When enabled, detected drift will be automatically synchronized to Teams without requiring manual Accept/Dismiss clicks</li>
            <li data-testid="user-guide-drift-bypass-step-email-audit-li">Email notifications will still be sent for audit purposes</li>
            <li data-testid="user-guide-drift-bypass-step-save-li">Click "Save Changes" to apply the configuration</li>
          </ol>
          <Text style={{ marginTop: '12px', fontSize: '14px', fontWeight: '600' }} data-testid="user-guide-bypass-mode-vs-manual-approval-text">
            Bypass Mode vs. Manual Approval:
          </Text>
          <ul style={{ marginLeft: '20px', marginTop: '8px', lineHeight: '1.8' }} data-testid="user-guide-bypass-vs-manual-list-ul">
            <li data-testid="user-guide-bypass-vs-manual-manual-approval-li"><strong data-testid="user-guide-bypass-vs-manual-manual-approval-label-strong">Manual Approval (Default):</strong> Drift notifications appear in UI, requiring admin to click Accept or Dismiss for each change</li>
            <li data-testid="user-guide-bypass-vs-manual-auto-sync-li"><strong data-testid="user-guide-bypass-vs-manual-auto-sync-label-strong">Bypass Mode (Auto-Sync):</strong> Changes are automatically synchronized, email notifications sent for audit trail only</li>
          </ul>
          <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-recommended-use-bypass-mode-for-production-text">
            Recommended: Use bypass mode for production environments with trusted domain feeds and manual approval for testing/validation environments.
          </Text>
        </Card>
      </div>

      {/* API Integration */}
      <div className={styles.section} data-testid="user-guide-section-4-div">
        <div className={styles.sectionTitle} data-testid="user-guide-section-title-4-div">
          <ArrowSync24Regular data-testid="user-guide-arrow-sync24-regular" />
          <Text size={500} weight="semibold" data-testid="user-guide-api-integration-text">API Integration</Text>
        </div>

        <Card className={styles.card} data-testid="user-guide-api-integration-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-ppe-workspace-teams-feed-integration-text">PPE Workspace Teams Feed Integration</Text>} data-testid="user-guide-api-integration-card-header" />

          <Text style={{ marginBottom: '12px' }} data-testid="user-guide-the-system-integrates-with-the-ppe-text">
            The system integrates with the PPE (Pre-Production Environment) Workspace Teams Feed for automated domain updates.
          </Text>

          <Text weight="semibold" data-testid="user-guide-current-configuration-text">Current Configuration:</Text>
          <div className={styles.codeBlock} data-testid="user-guide-code-block-div">
            Feed URL: {(() => {
              try {
                return getRuntimeConfig().domainFeedUrl || 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';
              } catch {
                return 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';
              }
            })()}<br data-testid="user-guide-feed-config-line-break-url-br" />
            Authentication: Bearer Token<br data-testid="user-guide-feed-config-line-break-auth-br" />
            Sync Interval: 360 minutes (6 hours)<br data-testid="user-guide-feed-config-line-break-sync-interval-br" />
            Status: Active
          </div>

          <Text weight="semibold" style={{ marginTop: '16px' }} data-testid="user-guide-local-api-endpoints-text">Local API Endpoints:</Text>
          <div className={styles.codeBlock} data-testid="user-guide-code-block-2-div">
            GET /api/domains - List all synchronized domains<br data-testid="user-guide-api-endpoint-line-break-domains-br" />
            POST /api/lseg/sync - Trigger manual sync<br data-testid="user-guide-api-endpoint-line-break-sync-br" />
            GET /api/config - View feed configuration<br data-testid="user-guide-api-endpoint-line-break-config-br" />
            POST /api/lseg/test-connection - Test feed connectivity
          </div>

          <Text style={{ marginTop: '12px', fontSize: '14px', color: tokens.colorNeutralForeground2 }} data-testid="user-guide-the-feed-configuration-is-stored-in-text">
            The feed configuration is stored in the database and persists across deployments.
          </Text>
        </Card>
      </div>

      {/* Support */}
      <div className={styles.section} data-testid="user-guide-section-5-div">
        <div className={styles.sectionTitle} data-testid="user-guide-section-title-5-div">
          <People24Regular data-testid="user-guide-people24-regular" />
          <Text size={500} weight="semibold" data-testid="user-guide-support-resources-text">Support & Resources</Text>
        </div>

        <Card className={styles.card} data-testid="user-guide-support-resources-card">
          <CardHeader
            header={<Text weight="semibold" data-testid="user-guide-need-help-text">Need Help?</Text>} data-testid="user-guide-support-resources-card-header" />

          <Text style={{ lineHeight: '1.8' }} data-testid="user-guide-for-additional-support-or-questions-about-text">
            For additional support or questions about the ADM system:
          </Text>
          <ul style={{ marginLeft: '20px', marginTop: '12px', lineHeight: '1.8' }} data-testid="user-guide-support-resources-list-ul">
            <li data-testid="user-guide-support-resource-contact-admin-li">Contact your system administrator</li>
            <li data-testid="user-guide-support-resource-api-docs-li">Review the API documentation in Settings</li>
            <li data-testid="user-guide-support-resource-system-health-li">Check system health status for service availability</li>
            <li data-testid="user-guide-support-resource-support-ticket-li">Submit a support ticket through your organization's IT portal</li>
          </ul>
        </Card>
      </div>
    </div>);

};